import { api, apiUpload } from './api';

/** Настройки бота (зеркало zod-схемы backend vk-bot-settings.ts). */
export interface VkBotSettings {
  llmProvider: 'gigachat' | 'anthropic' | 'openai';
  tone: string;
  botName: string;
  maxRepliesPerUserPerPost: number;
  maxRepliesPerPost: number;
  stopWords: string[];
  knowledgeBase: string;
  /** Активные розыгрыши/конкурсы: что бот может говорить (условия, сроки). */
  contestNotes: string;
  workingHours: string;
  deliveryEnabled: boolean;
  replyDelaySeconds: number;
  temperature: number;
  maxTokens: number;
}

export interface VkBotGroup {
  id: string;
  label: string;
  vkGroupId: string;
  isEnabled: boolean;
  settings: VkBotSettings;
  cityId: string | null;
  tradingPointId: string | null;
  callbackOk: boolean;
  webhookUrl: string;
  lastEventAt: string | null;
  createdAt: string;
  stats?: { replied: number; skipped: number; failed: number };
}

export interface LlmProviderStatus {
  provider: 'gigachat' | 'anthropic' | 'openai';
  enabled: boolean;
  disabledReason: string | null;
}

export interface VkBotZone {
  id: string;
  name: string;
  minOrder: number;
  isActive: boolean;
  kmlFilename: string | null;
  pointsCount: number;
  createdAt: string;
}

export interface VkBotFeedItem {
  id: string;
  groupLabel: string;
  vkPostId: string;
  vkCommentId: string;
  commentUrl: string;
  fromName: string | null;
  fromId: string;
  text: string;
  postText: string | null;
  status: 'pending' | 'processing' | 'replied' | 'skipped' | 'failed';
  skipReason: string | null;
  replyText: string | null;
  llmProvider: string | null;
  llmDecision: unknown;
  addressExtracted: string | null;
  geoResult: {
    resolvedAddress?: string | null;
    zoneName?: string | null;
    minOrder?: number | null;
    imprecise?: boolean;
  } | null;
  errorText: string | null;
  isTest: boolean;
  createdAt: string;
  processedAt: string | null;
}

export interface TestReplyResult {
  replyText: string | null;
  decision: unknown;
  skipReason: string | null;
  geoResult: unknown;
  provider: string;
  tookMs: number;
}

export interface ZoneCheckResult {
  geocode: {
    lat: number;
    lon: number;
    resolvedAddress: string | null;
    precision: string | null;
  } | null;
  imprecise: boolean;
  zoneName: string | null;
  minOrder: number | null;
}

export interface VkBotTrainingExample {
  id: string;
  groupId: string | null;
  groupLabel: string | null;
  question: string;
  badReply: string | null;
  /** Варианты правильного ответа (минимум один). */
  goodReplies: string[];
  isActive: boolean;
  createdAt: string;
}

export const vkBotApi = {
  /** Статус ключа геокодера (настроен ли, откуда, маска). */
  geocoderStatus: () =>
    api.get<{ enabled: boolean; source: 'ui' | 'env' | null; maskedKey: string | null }>(
      '/vk-bot/geocoder',
    ),
  /** Сохранить ключ геокодера из UI (пустая строка — очистить UI-ключ). */
  setGeocoderKey: (key: string) =>
    api.patch<{ enabled: boolean; source: 'ui' | 'env' | null; maskedKey: string | null }>(
      '/vk-bot/geocoder',
      { key },
    ),
  llmStatus: () =>
    api.get<{ providers: LlmProviderStatus[]; geocoderEnabled: boolean }>(
      '/vk-bot/llm-status',
    ),

  listGroups: () => api.get<VkBotGroup[]>('/vk-bot/groups'),
  getGroup: (id: string) => api.get<VkBotGroup>(`/vk-bot/groups/${id}`),
  connect: (body: { accessToken: string; cityId?: string | null }) =>
    api.post<{ group: VkBotGroup; callbackOk: boolean; callbackError?: string }>(
      '/vk-bot/groups',
      body,
    ),
  updateGroup: (
    id: string,
    patch: {
      isEnabled?: boolean;
      settings?: VkBotSettings;
      cityId?: string | null;
      tradingPointId?: string | null;
    },
  ) => api.patch<VkBotGroup>(`/vk-bot/groups/${id}`, patch),
  removeGroup: (id: string) => api.delete<{ ok: boolean }>(`/vk-bot/groups/${id}`),
  refreshCallback: (id: string, accessToken?: string) =>
    api.post<{ ok: boolean; error?: string }>(
      `/vk-bot/groups/${id}/refresh-callback`,
      { accessToken: accessToken || undefined },
    ),

  listZones: (groupId: string) =>
    api.get<VkBotZone[]>(`/vk-bot/groups/${groupId}/zones`),
  uploadKml: (groupId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiUpload<{ created: number; names: string[] }>(
      `/vk-bot/groups/${groupId}/zones/upload-kml`,
      fd,
    );
  },
  updateZone: (
    zoneId: string,
    patch: { name?: string; minOrder?: number; isActive?: boolean },
  ) => api.patch<{ ok: boolean }>(`/vk-bot/zones/${zoneId}`, patch),
  removeZone: (zoneId: string) =>
    api.delete<{ ok: boolean }>(`/vk-bot/zones/${zoneId}`),
  importZonesFromCrm: (groupId: string) =>
    api.post<{ created: number; names: string[]; skippedExisting: number }>(
      `/vk-bot/groups/${groupId}/zones/import-from-crm`,
    ),

  getTraining: () =>
    api.get<{ globalKnowledge: string; examples: VkBotTrainingExample[] }>(
      '/vk-bot/training',
    ),
  saveGlobalKnowledge: (text: string) =>
    api.patch<{ ok: boolean }>('/vk-bot/training/global-knowledge', { text }),
  createExample: (body: {
    groupId?: string | null;
    question: string;
    badReply?: string | null;
    goodReplies: string[];
  }) => api.post<{ ok: boolean }>('/vk-bot/training/examples', body),
  updateExample: (
    id: string,
    patch: {
      question?: string;
      badReply?: string | null;
      goodReplies?: string[];
      isActive?: boolean;
    },
  ) => api.patch<{ ok: boolean }>(`/vk-bot/training/examples/${id}`, patch),
  removeExample: (id: string) =>
    api.delete<{ ok: boolean }>(`/vk-bot/training/examples/${id}`),

  checkAddress: (groupId: string, address: string) =>
    api.post<ZoneCheckResult>(`/vk-bot/groups/${groupId}/check-address`, {
      address,
    }),

  test: (
    groupId: string,
    body: {
      postText?: string;
      commentText: string;
      authorName?: string;
      provider?: 'gigachat' | 'anthropic' | 'openai';
    },
  ) => api.post<TestReplyResult>(`/vk-bot/groups/${groupId}/test`, body),

  feed: (params: { groupId?: string; status?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params.groupId) q.set('groupId', params.groupId);
    if (params.status) q.set('status', params.status);
    if (params.limit) q.set('limit', String(params.limit));
    if (params.offset) q.set('offset', String(params.offset));
    return api.get<{ total: number; items: VkBotFeedItem[] }>(
      `/vk-bot/feed?${q.toString()}`,
    );
  },
};
